// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ReviewPreflight.test.tsx
import { type TFunction } from "i18next";

/** The six standard reviewers, as warhol names them. */
export const STANDARD_REVIEW_IDS = [
  "regulated-copy",
  "nutrition",
  "codes-values",
  "completeness",
  "variants",
  "copy-diff",
] as const;

export type StandardReviewId = (typeof STANDARD_REVIEW_IDS)[number];

export type ReviewPreflightGroup = "own" | "experts" | "standard";

export const REVIEW_PREFLIGHT_GROUPS: readonly ReviewPreflightGroup[] = [
  "own",
  "experts",
  "standard",
];

/**
 * The token warhol appends to an expert's name. Carrie replaces it with the
 * localized suffix. The statement under the row is translated below only when
 * this catalog has that exact sentence.
 */
export const EXPERT_REVIEW_SUFFIX_TOKEN = "{expertReviewSuffix}";

/**
 * Expert statements we ship a translation for, keyed by the stored sentence.
 * A statement that is not here stays as the expert wrote it.
 */
const METHOD_STATEMENT_KEYS: Readonly<Record<string, string>> = {
  "Start with the job's brief to understand the full picture.":
    "reviewPreflight.methodStatements.jobBrief",
};

const LEGACY_EXPERT_SUFFIXES = ["'s review", "'s method"] as const;

const STANDARD_REVIEW_ID_SET = new Set<string>(STANDARD_REVIEW_IDS);

export type ReviewPreflightRow = {
  id: string;
  label: string;
  description?: string;
  group?: ReviewPreflightGroup | null;
  /** Display-only statement per locale. Absent means the stored description. */
  display?: Record<string, string>;
};

export function isStandardReviewId(id: string): id is StandardReviewId {
  return STANDARD_REVIEW_ID_SET.has(id);
}

export function expertReviewName(label: string, t: TFunction): string {
  const suffix = t("reviewPreflight.expertSuffix");
  if (label.endsWith(EXPERT_REVIEW_SUFFIX_TOKEN)) {
    return `${label.slice(0, -EXPERT_REVIEW_SUFFIX_TOKEN.length)}${suffix}`;
  }
  for (const token of LEGACY_EXPERT_SUFFIXES) {
    if (label.endsWith(token)) {
      return `${label.slice(0, -token.length)}${suffix}`;
    }
  }
  return label;
}

function localeBase(language: string): string {
  const [base] = language.split("-");
  return base || "en";
}

function cachedStatement(
  category: ReviewPreflightRow,
  language: string,
): string | null {
  const line = category.display?.[localeBase(language)];
  if (line == null || line.trim() === "") return null;
  return line;
}

function methodStatement(description: string, t: TFunction): string {
  const key = METHOD_STATEMENT_KEYS[description.trim()];
  if (key == null) return description;
  const translated = t(key);
  // why: a missing catalog entry comes back as the key. The stored sentence
  // stays, and this lookup never writes the expert's method.
  if (translated === key || translated.trim() === "") return description;
  return translated;
}

/**
 * The name and one-line description Carrie draws for a picker row.
 * Standard rows come from the locale, keyed by id. An expert row's name is
 * the person's name plus the localized suffix. The description is the
 * statement they wrote, in the UI language when that sentence is translated.
 */
export function reviewPreflightRowCopy(
  category: ReviewPreflightRow,
  t: TFunction,
  language = "en",
): { name: string; description: string } {
  if (category.id.startsWith("expert-method:")) {
    return {
      name: expertReviewName(category.label, t),
      description:
        cachedStatement(category, language) ??
        methodStatement(category.description ?? "", t),
    };
  }
  if (isStandardReviewId(category.id)) {
    return {
      name: t(`reviewPreflight.checks.${category.id}.name`),
      description: t(`reviewPreflight.checks.${category.id}.description`),
    };
  }
  return {
    name: category.label,
    description: category.description ?? "",
  };
}
